'use client';
// Landing Page TanStack Query Hooks
import { useQuery } from '@tanstack/react-query';

import { useSafeLanguage } from '@/contexts/LanguageContext';
import { landingApi } from '@/services/api';
import type { CategoriesResponse, Category, Tag, TagsResponse } from '@/types';

// Query Keys Factory
export const LANDING_QUERY_KEYS = {
  all: ['landing'] as const,
  categories: (locale: string) => [...LANDING_QUERY_KEYS.all, 'categories', locale] as const,
  tags: (locale: string) => [...LANDING_QUERY_KEYS.all, 'tags', locale] as const,
};

/**
 * Fetch discovery categories from API
 */
export const useCategories = () => {
  const { language } = useSafeLanguage();
  return useQuery<CategoriesResponse, Error, Category[]>({
    queryKey: LANDING_QUERY_KEYS.categories(language.code),
    queryFn: () => landingApi.getCategories(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    select: (response) => response?.data ?? [],
  });
};

/**
 * Fetch discovery tags from API
 */
export const useTags = () => {
  const { language } = useSafeLanguage();
  return useQuery<TagsResponse>({
    queryKey: LANDING_QUERY_KEYS.tags(language.code),
    queryFn: () => landingApi.getTags(),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch tags by category slug from API
 */
export const useTagsBySlug = (slug: string) => {
  const { language } = useSafeLanguage();
  return useQuery<TagsResponse, Error, Tag[]>({
    queryKey: [...LANDING_QUERY_KEYS.tags(language.code), slug],
    queryFn: () => landingApi.getTagsBySlug(slug),
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
    select: (response) => {
      if (!response?.success || !response?.data) return [{ label: 'All', slug: '' }];

      const tagsData = response.data;
      let visibleTags: Tag[] = [];

      if (Array.isArray(tagsData)) {
        visibleTags = tagsData;
      } else if (typeof tagsData === 'object' && tagsData !== null) {
        visibleTags = Object.values(tagsData).filter(
          (item) => item && typeof item === 'object' && item.label && item.slug,
        );
      }

      return visibleTags;
    },
  });
};
